/**
 * The app's standard tab strip.
 *
 * One selected tab, clearly: it carries an indigo underline and indigo text at
 * full weight, while the rest are muted until hovered — so a person can tell
 * what they are looking at without reading every tab. Presentational only; the
 * caller owns which tab is active and what switching does, so the same strip
 * serves a URL-synced group screen and a local-state dashboard alike.
 */
import { Icon, piIconName } from './icon';

export interface TabItem {
  key: string;
  label: string;
  /** Full PrimeIcons class, e.g. `pi pi-key`. Optional. */
  icon?: string;
}

interface TabBarProps {
  tabs: TabItem[];
  activeKey: string;
  onChange: (key: string) => void;
  className?: string;
  /** When set, each tab gets `data-testid="<prefix>.<key>"`. */
  testIdPrefix?: string;
}

const TabBar = ({ tabs, activeKey, onChange, className, testIdPrefix }: TabBarProps) => (
  <div className={`flex flex-wrap gap-1 border-b border-line ${className || ''}`}>
    {tabs.map((t) => {
      const on = t.key === activeKey;
      // A legacy `pi pi-*` icon renders as Lucide when mapped, otherwise stays a
      // PrimeIcon — so tabs convert with no change to the group configs.
      const lucide = piIconName(t.icon);
      return (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          data-testid={testIdPrefix ? `${testIdPrefix}.${t.key}` : undefined}
          aria-current={on ? 'page' : undefined}
          className={`-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
            on
              ? 'border-indigo-600 text-indigo-700 dark:border-indigo-400 dark:text-indigo-300'
              : 'border-transparent text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-300'}`}
        >
          {lucide ? <Icon name={lucide} size={16} /> : (t.icon && <i className={`${t.icon} text-sm`} />)}
          {t.label}
        </button>
      );
    })}
  </div>
);

export default TabBar;
